import { useEffect, useRef } from "react";
import { useImmer } from "use-immer";

import {
  type RuleGraphRunWalkFrame,
  type RuleGraphRunWalkPlan,
  runWalkFrameAt,
} from "./run-walk";

const TICK_MS = 50;

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const settledFrame = (plan: RuleGraphRunWalkPlan): RuleGraphRunWalkFrame => ({
  beatIndex: plan.beats.length - 1,
  done: true,
});

/**
 * The run walk's clock: drives {@link runWalkFrameAt} off the wall clock
 * (`performance.now()` + interval, the useBuildingPlanProgress idiom, so a
 * throttled tab resumes at the right beat), clearing itself once the walk
 * settles.
 *
 * `runKey` is the replay identity — `${scenarioName}:${runToken}` — because
 * the evaluation query is cached forever and a chip re-select never
 * refetches. A key change resets DURING RENDER, not in an effect: on a
 * cache hit the new scenario's final overlays are already present the
 * render the key changes, and an effect-based reset would flash one fully
 * settled frame before the walk dims everything back down.
 *
 * `prefers-reduced-motion` settles instantly: a CSS guard cannot stop a JS
 * timer, so the clock itself checks.
 *
 * Returns null while there is nothing to walk (no key, or the plan hasn't
 * arrived under the current key yet).
 */
export function useRuleGraphRunWalk({
  plan,
  runKey,
}: {
  plan: RuleGraphRunWalkPlan | null;
  runKey: string | null;
}): RuleGraphRunWalkFrame | null {
  const initialFrame = (): RuleGraphRunWalkFrame | null => {
    if (runKey === null || plan === null) return null;
    return prefersReducedMotion()
      ? settledFrame(plan)
      : { beatIndex: 0, done: false };
  };

  const [state, setState] = useImmer<{
    runKey: string | null;
    frame: RuleGraphRunWalkFrame | null;
  }>({ runKey: null, frame: null });

  // Adjust-state-during-render: a new run key restarts the walk before this
  // frame ever paints.
  const keyChanged = state.runKey !== runKey;
  if (keyChanged) {
    const frame = initialFrame();
    setState(() => ({ runKey, frame }));
  }

  // The interval reads the latest plan through a ref: the plan object is
  // rebuilt every render, and re-keying the effect on its identity would
  // restart the walk each time anything re-renders.
  const planRef = useRef(plan);
  planRef.current = plan;
  const hasPlan = plan !== null;
  const hasStartedFrame = !keyChanged && state.frame !== null;

  useEffect(() => {
    if (runKey === null || !hasPlan) return;
    const currentPlan = planRef.current;
    if (currentPlan === null) return;
    if (prefersReducedMotion()) {
      setState((draft) => {
        draft.frame = settledFrame(currentPlan);
      });
      return;
    }
    // The plan can arrive a render after the key (a cache miss): start the
    // clock then, under the same key.
    setState((draft) => {
      draft.frame ??= { beatIndex: 0, done: false };
    });
    const origin = performance.now();
    const interval = setInterval(() => {
      const latestPlan = planRef.current;
      if (latestPlan === null) return;
      const frame = runWalkFrameAt(latestPlan, performance.now() - origin);
      setState((draft) => {
        draft.frame = frame;
      });
      if (frame.done) clearInterval(interval);
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [runKey, hasPlan]);

  if (keyChanged) return initialFrame();
  return hasStartedFrame ? state.frame : initialFrame();
}
